import BizInput from '../../../../../../FrontEndReact/src/Core/Components/Mini/BizInput';
import { BizButton } from '../../../../../../FrontEndReact/src/Core/Components/Mini/BizButton';
import BizUnitSelect from '../../../../../../FrontEndReact/src/Core/Components/Mini/BizUnitSelect';
import { BizSelect } from '../../../../../../Js/React/UI/BizSelect/BizSelect';
import { TFunction } from 'i18next';
import { useLaxhjalpenStudentRegistration } from '../../Hooks/useStudentRegistration';

type Registration = ReturnType<typeof useLaxhjalpenStudentRegistration>;

type LaxhjalpenStudentRegistrationFormProps = {
	registration: Registration;
	t: TFunction;
	variant: 'admin' | 'public';
};

const LaxhjalpenStudentRegistrationForm = ({ registration, t, variant }: LaxhjalpenStudentRegistrationFormProps) => {
	const isAdmin = variant === 'admin';
	const sectionClassName = isAdmin ? 'admin-panel' : 'biz-laxhjalpen-student-registration-public__panel';
	const secondSectionClassName = isAdmin ? 'admin-panel margin-top-20' : 'biz-laxhjalpen-student-registration-public__panel';
	const fieldClassName = isAdmin ? 'margin-top' : 'biz-laxhjalpen-student-registration-public__field';
	const labelClassName = isAdmin ? 'control-label' : 'biz-laxhjalpen-student-registration-public__label';
	const sectionTitleClassName = `${labelClassName} biz-laxhjalpen-student-registration__section-title`;
	const gridClassName = isAdmin ? 'admin-two-col margin-top-20' : 'biz-laxhjalpen-student-registration-public__grid';

	return (
		<div className={gridClassName}>
			<div>
				<div className={sectionClassName}>
					<span className={sectionTitleClassName}>{t('ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.STUDENT_SECTION')}</span>
					<div className={fieldClassName}>
						<span className={labelClassName}>{t('ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.EMAIL')}</span>
						<BizInput
							className='form-control'
							name='StudentEmail'
							type='email'
							value={registration.student.email}
							onChange={(event) => registration.updateStudentField('email', event.target.value)}
							formConfig={{
								state: registration.formState,
								validators: [registration.requiredValidator, registration.emailValidator],
							}}
							legacy={isAdmin}
						/>
					</div>
					<div className={fieldClassName}>
						<span className={labelClassName}>{t('ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.FIRST_NAME')}</span>
						<BizInput
							className='form-control'
							name='StudentFirstName'
							type='text'
							value={registration.student.firstName}
							onChange={(event) => registration.updateStudentField('firstName', event.target.value)}
							formConfig={{ state: registration.formState, validators: [registration.requiredValidator] }}
							legacy={isAdmin}
						/>
					</div>
					<div className={fieldClassName}>
						<span className={labelClassName}>{t('ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.LAST_NAME')}</span>
						<BizInput
							className='form-control'
							name='StudentLastName'
							type='text'
							value={registration.student.lastName}
							onChange={(event) => registration.updateStudentField('lastName', event.target.value)}
							formConfig={{ state: registration.formState, validators: [registration.requiredValidator] }}
							legacy={isAdmin}
						/>
					</div>
					<div className={fieldClassName}>
						<span className={labelClassName}>{t('ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.SSN')}</span>
						<BizInput
							className='form-control'
							name='StudentSsn'
							type='text'
							value={registration.student.ssn}
							onChange={(event) => registration.updateStudentField('ssn', event.target.value)}
							formConfig={{ state: registration.formState, validators: [registration.ssnValidator] }}
							legacy={isAdmin}
						/>
						{registration.ssnValidationMessageKey && (
							<div className='biz-laxhjalpen-student-registration__validation-message' role='alert'>
								{t(`ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.${registration.ssnValidationMessageKey}`)}
							</div>
						)}
					</div>
					<div className={fieldClassName}>
						<span className={labelClassName}>{t('ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.UNIT')}</span>
						<BizUnitSelect
							accessTypeKey='USERS'
							name='StudentUnit'
							selectedId={registration.student.unitId}
							onChange={(unit) => {
								registration.updateStudentField('unitId', unit?.Id ?? -1);
								registration.updateStudentField('unitTypeId', unit?.UnitTypeId ?? -1);
								registration.updateStudentField('homeworkHelperUserId', -1);
							}}
							noSelectText={t('ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.NO_UNIT')}
							childNoSelectText={t('ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.CHILD_NO_UNIT')}
							direction='horizontal'
							formConfig={{ state: registration.formState, validators: [registration.requiredValidator] }}
						/>
						{registration.shouldShowUnitTypeValidation && (
							<div className='biz-laxhjalpen-student-registration__validation-message'>
								{t('ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.UNIT_MUST_BE_GROUP')}
							</div>
						)}
					</div>
				</div>
				<div className={secondSectionClassName}>
					<span className={sectionTitleClassName}>{t('ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.HELPER_SECTION')}</span>
					<div className={fieldClassName}>
						{/* <span className={labelClassName}>{t('ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.HOMEWORK_HELPER')}</span> */}
						<BizSelect
							name='HomeworkHelper'
							className='form-control'
							options={registration.helperOptions}
							selectedId={registration.student.homeworkHelperUserId}
							onChange={(helper) => registration.updateStudentField('homeworkHelperUserId', helper?.Id ?? -1)}
							noSelectText={t('ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.SELECT_HOMEWORK_HELPER')}
							formConfig={
								registration.helperRequired
									? { state: registration.formState, validators: [registration.requiredValidator] }
									: undefined
							}
							disabled={registration.student.unitId <= 0}
						/>
						<div className='biz-laxhjalpen-student-registration__hint'>
							{t('ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.HELPER_OPTIONAL_HINT')}
						</div>
						{registration.student.unitId > 0 && registration.helperOptions.length === 0 && (
							<div className='alert alert-warning margin-top-10'>
								{t('ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.NO_HELPERS')}
							</div>
						)}
					</div>
				</div>
			</div>
			<div>
				<div className={sectionClassName}>
					<div className='biz-laxhjalpen-student-registration__guardians-header'>
						<span className={sectionTitleClassName}>
							{t('ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.GUARDIAN_SECTION')}
						</span>
						<BizButton
							text={t('ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.ADD_GUARDIAN')}
							onClick={() => registration.addGuardian()}
							icon='fa-solid fa-plus'
							legacy={isAdmin}
						/>
					</div>
					<div className='biz-laxhjalpen-student-registration__guardian-list'>
						{registration.guardians.map((guardian, index) => (
							<div className='biz-laxhjalpen-student-registration__guardian-card' key={guardian.id}>
								<div className='biz-laxhjalpen-student-registration__guardian-card-header'>
									<span className={labelClassName}>
										{t('ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.GUARDIAN_TITLE', { index: index + 1 })}
									</span>
									{registration.guardians.length > 1 && (
										<BizButton
											text={t('ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.REMOVE_GUARDIAN')}
											onClick={() => registration.removeGuardian(guardian.id)}
											icon='far fa-times'
											legacy={isAdmin}
										/>
									)}
								</div>
								<div className={fieldClassName}>
									<span className={labelClassName}>{t('ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.EMAIL')}</span>
									<BizInput
										className='form-control'
										name={`${guardian.id}_email`}
										type='email'
										value={guardian.email}
										onChange={(event) =>
											registration.updateGuardianField(guardian.id, 'email', event.target.value)
										}
										formConfig={{
											state: registration.formState,
											validators: [registration.requiredValidator, registration.emailValidator],
										}}
										legacy={isAdmin}
									/>
								</div>
								<div className={fieldClassName}>
									<span className={labelClassName}>{t('ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.MOBILE')}</span>
									<BizInput
										className='form-control'
										name={`${guardian.id}_mobile`}
										type='tel'
										maxLength={50}
										value={guardian.mobile}
										onChange={(event) =>
											registration.updateGuardianField(guardian.id, 'mobile', event.target.value)
										}
										legacy={isAdmin}
									/>
								</div>
								<div className={fieldClassName}>
									<span className={labelClassName}>
										{t('ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.FIRST_NAME')}
									</span>
									<BizInput
										className='form-control'
										name={`${guardian.id}_firstName`}
										type='text'
										value={guardian.firstName}
										onChange={(event) =>
											registration.updateGuardianField(guardian.id, 'firstName', event.target.value)
										}
										formConfig={{
											state: registration.formState,
											validators: [registration.requiredValidator],
										}}
										legacy={isAdmin}
									/>
								</div>
								<div className={fieldClassName}>
									<span className={labelClassName}>{t('ADMIN.LAXHJALPEN.STUDENT_REGISTRATION.LAST_NAME')}</span>
									<BizInput
										className='form-control'
										name={`${guardian.id}_lastName`}
										type='text'
										value={guardian.lastName}
										onChange={(event) =>
											registration.updateGuardianField(guardian.id, 'lastName', event.target.value)
										}
										formConfig={{
											state: registration.formState,
											validators: [registration.requiredValidator],
										}}
										legacy={isAdmin}
									/>
								</div>
							</div>
						))}
					</div>
				</div>
			</div>
		</div>
	);
};

export default LaxhjalpenStudentRegistrationForm;
